import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MapPin, AlertCircle, Loader2, ScanFace, Video } from "lucide-react";
import scanQrIcon from "../assets/qr-code.png";
import assignDeptIcon from "../assets/delegation.png";
import { useAuth } from "../context/auth-context";
import searchIcon from "../assets/search.svg";
import attendanceIcon from "../assets/attendance.svg";
import { LogOut } from "lucide-react";
import { toast } from "react-toastify";
import referralIcon from "../assets/referralIcon.png";

const SupervisorView = () => {
  const [showSearchModal, setShowSearchModal] = useState(false); 
  const [searchTab, setSearchTab] = useState("phone"); // "phone" | "emp"
  const [searchEmpCode, setSearchEmpCode] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const { fetchNearestCompany, logout, cacheLocation, cacheCompany, token, admin } = useAuth();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [isUTVMember, setIsUTVMember] = useState(false);
  const [featureAccess, setFeatureAccess] = useState([]);
  const [userRole, setUserRole] = useState("");

  // ── Geolocation state ──────────────────────────────────────────────────
  const [coords, setCoords] = useState({ latitude: null, longitude: null });
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationChecked, setLocationChecked] = useState(false);

  // Prevent duplicate geolocation calls across re-renders / permission events
  const locationRequestedRef = useRef(false);

  // ── Nearest-company state ─────────────────────────────────────────────
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyChecked, setCompanyChecked] = useState(false);
  const [showNotNearModal, setShowNotNearModal] = useState(false);

  // ── Action (button-triggered) location + nearest-company flow ─────────
  const [actionLoading, setActionLoading] = useState(null); // 'scan' | 'face' | 'assign' | null
  const [actionStage, setActionStage] = useState(""); // "location" | "company"
  const [actionError, setActionError] = useState("");

  // ── Helper: get a fresh, one-off geolocation reading, promise-based ────
  const getFreshLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const freshCoords = { latitude, longitude };
          setCoords(freshCoords);
          cacheLocation(freshCoords);
          resolve(freshCoords);
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }, [cacheLocation]);

  // ── Helper: fresh location → resolve nearest company → navigate ────────
  const handleActionWithLocation = useCallback(
    async (actionKey, targetRoute) => {
      setActionError("");
      setActionLoading(actionKey);
      try {
        setActionStage("location");
        const freshCoords = await getFreshLocation();

        setActionStage("company");
        const data = await fetchNearestCompany({
          latitude: freshCoords.latitude,
          longitude: freshCoords.longitude,
        });
        const resolvedCompany = data?.company || data;
        const companyId = resolvedCompany?._id || resolvedCompany?.companyId;

        if (!companyId) {
          setCompany(null);
          setShowNotNearModal(true);
          return;
        }

        const resolved = {
          _id: companyId,
          name: resolvedCompany.companyName,
          code: resolvedCompany.companyCode,
        };
        setCompany(resolved);
        cacheCompany(resolved);

        navigate(targetRoute, {
          state: { company: resolved, location: freshCoords },
        });
      } catch (err) {
        setActionError(
          "We couldn't verify your location or nearby company. Please enable location access and try again."
        );
      } finally {
        setActionLoading(null);
        setActionStage("");
      }
    },
    [getFreshLocation, fetchNearestCompany, cacheCompany, navigate]
  );

  const handleScanQR = () => handleActionWithLocation("scan", "/lkart/admin/attendance");
  const handleFaceScan = () => handleActionWithLocation("face", "/lkart/admin/face-scan");
  const handleAssignDepartment = () => handleActionWithLocation("assign", "/lkart/admin/assign");

  const goToReferAndEarnPage = () => {
  navigate("/lkart/admin/refer-and-earn/admin");
};

  const goToMyVideos = () => {
    navigate("/lkart/admin/tutorials");
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleViewAttendance = () => {
    navigate("/lkart/admin/view-attendance");
  };

  const handleLogoutNotNear = async () => {
    await logout();
    setShowNotNearModal(false);
    navigate("/lkart/admin/");
  };

  const handleSearchWorker = async () => {
    // Validation based on active tab
    if (searchTab === "phone") {
      if (!searchPhone.trim()) {
        toast.error("Please enter a phone number");
        return;
      }
      if (!validatePhoneNumber(searchPhone)) {
        toast.error("Please enter a valid 10-digit phone number");
        return;
      }
    } else {
      if (!searchEmpCode.trim()) {
        toast.error("Please enter an Worker code");
        return;
      }
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem("admin_auth_token");

      // Build payload based on active tab
      const payload =
        searchTab === "phone"
          ? { phone: searchPhone.trim() }
          : { workerCode: searchEmpCode.trim() };

      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/search-worker`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("search worker", response);

      if (response.data.success && response.data.workerId) {
        const workerId = response.data.workerId;
        toast.success(response.data.message || "Worker found!");
        navigate(`/lkart/admin/attendance-history/${workerId}`);
        setShowSearchModal(false);
        setSearchPhone("");
        setSearchEmpCode("");
        setSearchTab("phone");
      } else {
        toast.error(
          searchTab === "phone"
            ? "No worker found for this phone number"
            : "No worker found for this worker code"
        );
      }
    } catch (error) {
      console.error("Search worker error:", error);

      if (error.response) {
        if (error.response.status === 404) {
          toast.error(
            searchTab === "phone"
              ? "No worker found for this phone number"
              : "No worker found for this worker code"
          );
        } else if (error.response.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } else if (error.request) {
        toast.error("Network error. Please try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSearching(false);
    }
  };

  const features = [
  {
    name: "Scan QR",
    icon: scanQrIcon,
    handler: handleScanQR,
  },
  { name: "Face Scan", icon: ScanFace, handler: handleFaceScan, isLucide: true },
  {
    name: "Assign Department",
    icon: assignDeptIcon,
    handler: handleAssignDepartment,
  },
  {
    name: "View Attendance",
    icon: attendanceIcon,
    handler: handleViewAttendance,
  },
  {
    name: "Search Worker",
    icon: searchIcon,
    handler: () => setShowSearchModal(true),
  },
  {
    name: "Refer & Earn",
    icon: referralIcon,
    handler: goToReferAndEarnPage,
  },
  { name: "Tutorials", icon: Video, handler: goToMyVideos, isLucide: true },
];

  return (
    <>
      <div className="min-h-screen overflow-hidden bg-gray-100 p-4 sm:p-6 lg:p-8">
  <div
    className="w-full h-full bg-white shadow-lg rounded-lg px-4 sm:px-6 pb-8 sm:pb-10"
    style={{ paddingTop: 'calc(var(--header-height) + 16px)' }}
  >
    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-2">
      On-Demand Supervisor Dashboard
    </h1>

    {company?.name && (
      <p className="text-center pt-2 text-md sm:text-base font-semibold text-indigo-600 mb-2">
        {company.name} ({company.code})
      </p>
    )}
    {!company?.name && <div className="mb-2" />}

    {actionError && (
      <p className="text-center text-sm text-red-500 mb-4 px-4">{actionError}</p>
    )}
    <div className={actionError ? "mb-4" : "mb-8 sm:mb-12"} />

    {/* 2-tile grid */}
    <div className="grid grid-cols-2 max-w-xl mx-auto gap-3 sm:gap-6">
  {features.map((feature) => (
    <button
      key={feature.name}
      onClick={feature.handler}
      disabled={actionLoading !== null}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-3 sm:p-6 flex flex-col items-center justify-center space-y-2 sm:space-y-3 border border-gray-200 hover:border-indigo-300 min-h-[110px] sm:min-h-[140px] w-full group disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center transition-transform group-hover:scale-110">
        {feature.isLucide ? (
          <feature.icon className="w-full h-full text-indigo-600" strokeWidth={1.5} />
        ) : (
          <img src={feature.icon} alt={feature.name} className="w-full h-full object-contain" />
        )}
      </div>
      <span className="text-xs sm:text-base font-semibold text-indigo-700 text-center leading-tight">
        {feature.name}
      </span>
    </button>
  ))}
</div>
  </div>
</div>

      {/* Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-sm w-full max-w-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Search Worker</h3>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                onClick={() => setSearchTab("phone")}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  searchTab === "phone"
                    ? "text-indigo-600 border-b-2 border-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Phone Number
              </button>
              <button
                onClick={() => setSearchTab("emp")}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  searchTab === "emp"
                    ? "text-indigo-600 border-b-2 border-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Worker Code
              </button>
            </div>

            {/* Phone Tab Panel */}
            {searchTab === "phone" && (
              <>
                <input
                  type="tel"
                  value={searchPhone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) setSearchPhone(value);
                  }}
                  placeholder="Enter phone number (e.g., 9998877445)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={isSearching}
                  maxLength={10}
                />
                <div className="text-sm text-gray-500 mb-4">
                  Enter 10-digit phone number without country code
                </div>
              </>
            )}

            {/* Worker Code Tab Panel */}
            {searchTab === "emp" && (
              <>
                <input
                  type="text"
                  value={searchEmpCode}
                  onChange={(e) => setSearchEmpCode(e.target.value)}
                  placeholder="Enter worker code (e.g., FAC0023343)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={isSearching}
                />
                <div className="text-sm text-gray-500 mb-4">
                  Enter the unique worker code assigned to the worker
                </div>
              </>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchPhone("");
                  setSearchEmpCode("");
                  setSearchTab("phone");
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                disabled={isSearching}
              >
                Cancel
              </button>
              <button
                onClick={handleSearchWorker}
                disabled={
                  isSearching ||
                  (searchTab === "phone" && searchPhone.length !== 10) ||
                  (searchTab === "emp" && !searchEmpCode.trim())
                }
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Searching...
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No nearby company found — shown after a Scan QR / Face Scan / Assign Department click */}
      {showNotNearModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm p-6 flex flex-col items-center gap-4 text-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">No nearby company found</h2>
            <p className="text-sm text-gray-500 mt-1">
              Move closer to a registered company and try again.
            </p>
            <button
              onClick={() => setShowNotNearModal(false)}
              className="w-full py-2.5 text-sm font-semibold bg-red-500 text-white rounded-xl hover:bg-red-600 active:scale-95 transition-all"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Full-page modal shown while resolving location/company for a clicked action */}
      {actionLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-100 p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm p-8 flex flex-col items-center gap-4 text-center">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {actionStage === "company" ? "Checking nearby company" : "Getting your location"}
              </h2>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">This can take a few seconds</p>
            </div>
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
          </div>
        </div>
      )}
    </>
  );
};

export default SupervisorView;